import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { documentAPI } from '../../services/api';
import { useSystemTotal } from '../../hooks/useSystemTotal';
import {
  type VehicleComplianceRow,
  type VehicleComplianceSummary,
  formatDocExpiry,
  getExpiryStatusMeta,
} from '../../constants/documentTypes';
import { brandColors } from '../../theme/appTheme';

const FILTERS = [
  { id: 'all', label: 'All vehicles' },
  { id: 'expired', label: '🔴 Expired' },
  { id: 'expiring_7', label: '🟠 Within 7 days' },
  { id: 'expiring_30', label: '🟡 Within 30 days' },
  { id: 'valid', label: '🟢 Valid' },
  { id: 'missing', label: 'Not uploaded' },
  { id: 'alerts', label: 'Alerts only' },
];

export const VehicleDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'all';
  const [summary, setSummary] = useState<VehicleComplianceSummary | null>(null);
  const [vehicles, setVehicles] = useState<VehicleComplianceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const { total: systemTotal } = useSystemTotal('vehicles');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = status && status !== 'all' ? { status } : undefined;
      const res = await documentAPI.getVehicleCompliance(query);
      setSummary(res.data?.summary || null);
      setVehicles(res.data?.vehicles || []);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to load vehicle documents');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) => (
      v.vehicleNumber.toLowerCase().includes(q)
      || v.alerts.some((a) => a.label.toLowerCase().includes(q))
    ));
  }, [vehicles, search]);

  const setFilter = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('status');
    else next.set('status', id);
    setParams(next, { replace: true });
  };

  return (
    <Box>
      <PageHeader
        title="Vehicle Documents"
        subtitle={getPageSubtitle('vehicle_documents', 'Vehicle Documents', {
          total: summary?.vehiclesTracked ?? systemTotal,
        })}
        icon={<FolderIcon />}
        isCore
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 1.25,
        mb: 2,
      }}>
        {[
          { key: 'expired', emoji: '🔴', label: 'Expired', value: summary?.expired ?? 0 },
          { key: 'expiring_7', emoji: '🟠', label: 'Expiring within 7 days', value: summary?.expiring_7 ?? 0 },
          { key: 'expiring_30', emoji: '🟡', label: 'Expiring within 30 days', value: summary?.expiring_30 ?? 0 },
          { key: 'valid', emoji: '🟢', label: 'Valid', value: summary?.valid ?? 0 },
        ].map((card) => {
          const meta = getExpiryStatusMeta(card.key);
          return (
            <Paper
              key={card.key}
              elevation={0}
              onClick={() => setFilter(card.key)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                borderRadius: 2,
                border: `1px solid ${status === card.key ? meta.color : meta.border}`,
                bgcolor: meta.bg,
              }}
            >
              <Typography fontSize={12} fontWeight={700} sx={{ color: meta.color }}>
                {card.emoji} {card.label}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: meta.color }}>
                {loading ? '—' : card.value}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      <Paper elevation={0} sx={{ mb: 2, p: 1.25, borderRadius: 2, border: `1px solid ${brandColors.border}` }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
          {FILTERS.map((f) => (
            <Chip
              key={f.id}
              label={f.label}
              size="small"
              clickable
              onClick={() => setFilter(f.id)}
              sx={{
                height: 26,
                fontWeight: status === f.id ? 700 : 500,
                bgcolor: status === f.id ? brandColors.primary : '#f1f5f9',
                color: status === f.id ? '#fff' : brandColors.textDark,
              }}
            />
          ))}
        </Box>
        <TextField
          size="small"
          fullWidth
          placeholder="Search vehicle number or document type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: `1px solid ${brandColors.border}`, borderRadius: 2 }}>
          <Typography color="text.secondary">No vehicles match this filter.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          {filtered.map((v) => {
            const overall = getExpiryStatusMeta(v.overallStatus);
            const lines = v.slots.filter((s) => s.required && s.document);
            return (
              <Paper
                key={v.vehicleId}
                elevation={0}
                onClick={() => navigate(`/vehicles/${v.vehicleId}`)}
                sx={{
                  p: 1.75,
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: `1px solid ${overall.border}`,
                  '&:hover': { boxShadow: '0 6px 18px rgba(15,23,42,0.08)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography fontWeight={800}>Vehicle: {v.vehicleNumber}</Typography>
                  <Chip
                    label={`${overall.emoji} ${overall.label}`}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: overall.bg, color: overall.color }}
                  />
                </Box>
                {lines.length === 0 ? (
                  <Typography fontSize={13} color="text.secondary">No documents uploaded yet.</Typography>
                ) : (
                  lines.map((slot) => {
                    const meta = getExpiryStatusMeta(slot.status);
                    return (
                      <Typography key={slot.documentType} fontSize={13} sx={{ color: meta.color, fontWeight: 600, lineHeight: 1.7 }}>
                        {meta.emoji} {slot.label} expires: {formatDocExpiry(slot.document?.expiryDate)}
                      </Typography>
                    );
                  })
                )}
                {v.slots.some((s) => s.required && s.status === 'missing') && (
                  <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }}>
                    Missing: {v.slots.filter((s) => s.required && s.status === 'missing').map((s) => s.label).join(', ')}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
